import { reportPageHeightMm } from './reportPageFormat';
import type { ReportPageOrientation, ReportPageSize } from './reportPageFormat';
import { ReportLogoImage } from './ReportLogoImage';

// ------------------------------------------------------------------
// ReportCoverPage — an optional front-matter page for a report (story
// 4-82; P06 §4). Purely presentational: no state, no fetch — callers who
// already have the render-model's title/template/subject/logo fields pass
// them straight through.
//
// Story 4-106: `minHeight` used to be a flat `min-h-[60vh]` — 60% of
// whatever ancestor establishes the viewport, not "one physical page." A
// free-standing viewport fraction inside `ReportDesignerCanvas`'s (then)
// generic reading-width column is exactly why this read as a small,
// wrongly-proportioned block rather than a cover page. `pageSize` (the
// template's own `ReportStyle.pageSize`, `contracts/reports.js`) now drives
// a real physical height via `reportPageFormat.ts` — the SAME source
// `ReportDesignerCanvas`'s page-shaped wrapper reads — so the cover reserves
// one full page's height, not a fraction of the browser window.
//
// WHY A NEW PRIMITIVE. Matches `ReportDataTable`'s own reasoning (see that
// file's header comment): the report renderer's hard "one design,
// consistent web/print/PDF" constraint (story 4-78's `a86b338` fix) rules
// out any existing app-shell surface (e.g. a `glass-card` panel) — this is
// a plain, print-safe block, semantic tokens plus caller-supplied accent
// only on a small decorative rule, never on text.
//
// a11y: `aria-hidden="true"`. Every fact this page shows (title, template
// name/version, subject) is repeated moments later in the report's REAL
// semantic header (`<h2>`, the template-line `<p>`) — this block exists
// only as visual front matter for print/PDF pagination, so hiding it from
// the accessibility tree avoids a screen reader announcing the same title
// and template line twice before reaching the report's actual content.
// ------------------------------------------------------------------
export interface ReportCoverPageProps {
  title: string;
  templateName?: string | null;
  templateVersion?: number | null;
  subjectName?: string | null;
  subjectAddress?: string | null;
  logo?: string | null;
  /** Tenant accent — used ONLY for the decorative rule's background, never for text (see file header). */
  accent?: string | null;
  /** The template's `ReportStyle.pageSize` — drives the cover's real physical height (story 4-106). Defaults to A4. */
  pageSize?: ReportPageSize | null;
  /** Story 4-114 — the template's `ReportStyle.pageOrientation`; swaps the reserved height in landscape. Defaults to portrait. */
  orientation?: ReportPageOrientation | null;
  /**
   * Story 4-120 — `ReportStyle.variants.cover`. `'standard'` (default) is
   * the pre-4-120 look unchanged (the decorative rule below is the only
   * accent usage); `'accent'` additionally paints a subtle tinted band
   * behind the title block, using the SAME `accent` prop (already resolved
   * through `colorRoles.brand` at the call site — `null`/absent renders no
   * band, same honest "nothing to tint with" fallback the rule below uses).
   */
  variant?: 'standard' | 'accent';
  className?: string;
}

export function ReportCoverPage({
  title,
  templateName,
  templateVersion,
  subjectName,
  subjectAddress,
  logo,
  accent,
  pageSize,
  orientation,
  variant = 'standard',
  className = '',
}: ReportCoverPageProps): JSX.Element {
  const subjectLine = [subjectName, subjectAddress].filter(Boolean).join(' — ');
  const accentBand = variant === 'accent' && accent;
  return (
    <div
      aria-hidden="true"
      className={`report-cover-page flex flex-col items-center justify-between px-10 py-12 text-center ${className}`}
      style={{ minHeight: `${reportPageHeightMm(pageSize, orientation)}mm` }}
    >
      {/* Real bug, found live (2026-09-09): this stray empty `<div />` used to sit
          here as a THIRD flex child alongside the title block and the rule below —
          with `justify-between` and three children (not the intended two), the
          title block (the middle child) landed roughly halfway down the page
          instead of at the top. Present since this component's first version,
          never caught because every prior check verified the cover's overall
          HEIGHT, never the internal position of its own children. Identical bug
          fixed in the same commit in `pdfExport.service.js#renderCoverPage`
          (the PDF twin, same stray div in the same position). */}
      <div
        className={`space-y-3 ${accentBand ? 'rounded-lg px-6 py-5' : ''}`}
        style={accentBand ? { backgroundColor: `${accent}14` } : undefined}
      >
        {logo && (
          <ReportLogoImage
            src={logo}
            fallbackText={templateName || title || 'Report logo'}
            className="mx-auto max-h-20 max-w-[220px] object-contain"
          />
        )}
        <p className="text-2xl font-bold text-foreground">{title}</p>
        {templateName && (
          <p className="text-sm text-muted-foreground">
            {templateName}
            {templateVersion != null ? ` v${templateVersion}` : ''}
          </p>
        )}
        {subjectLine && <p className="text-sm text-foreground">{subjectLine}</p>}
      </div>
      <div className="h-1 w-16 rounded-full" style={{ backgroundColor: accent || '#94a3b8' }} />
    </div>
  );
}
